import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ShowOneProduct.css';
import { API_BASE } from '../api';
import { useCart } from '../context/CartContext';

const ProductDetail = () => {
    const { id } = useParams();
    const { addToCart } = useCart();
    const [product, setProduct] = useState(null);
    const [productLoadFailed, setProductLoadFailed] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [reviewForm, setReviewForm] = useState({
        reviewerName: '',
        rating: 5,
        comment: '',
    });
    const [submittingReview, setSubmittingReview] = useState(false);
    const [justAddedToCartId, setJustAddedToCartId] = useState(null);

    const renderStars = (ratingValue) => {
        const rounded = Math.max(0, Math.min(5, Math.round(Number(ratingValue) || 0)));
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(<span key={i} className={i <= rounded ? 'active' : ''}>★</span>);
        }
        return <div className="review-stars">{stars}</div>;
    };

    function loadProductAndReviews(pid) {
        const numId = Number(pid);
        if (!numId || isNaN(numId)) return;
        setProduct(null);
        setProductLoadFailed(false);
        fetch(API_BASE + '/' + numId)
            .then((r) => {
                if (!r.ok) {
                    setProductLoadFailed(true);
                    return null;
                }
                return r.json();
            })
            .then((data) => {
                if (data && typeof data === 'object' && data._id != null) {
                    setProduct(data);
                    setProductLoadFailed(false);
                    loadReviews(numId);
                } else {
                    setProductLoadFailed(true);
                }
            })
            .catch(() => setProductLoadFailed(true));
    }

    function loadReviews(pid) {
        const numId = Number(pid);
        if (!numId || isNaN(numId)) return;
        setReviewsLoading(true);
        fetch(`${API_BASE}/reviews/${numId}`)
            .then((r) => r.json())
            .then((data) => setReviews(Array.isArray(data) ? data : []))
            .catch(() => setReviews([]))
            .finally(() => setReviewsLoading(false));
    }

    useEffect(() => {
        if (id) loadProductAndReviews(id);
    }, [id]);

    function handleSubmitReview(e) {
        e.preventDefault();
        if (!product?._id || submittingReview) return;
        setSubmittingReview(true);
        const payload = {
            productId: Number(product._id),
            reviewerName: reviewForm.reviewerName || 'Anonymous',
            rating: Number(reviewForm.rating),
            comment: reviewForm.comment || '',
        };
        fetch(`${API_BASE}/reviews`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then((r) => r.json())
            .then((data) => {
                if (data && data.review) {
                    setReviewForm({ reviewerName: '', rating: 5, comment: '' });
                    loadProductAndReviews(product._id);
                } else {
                    alert('Could not submit review.');
                }
            })
            .catch((err) => {
                console.error('Review submit failed:', err);
                alert('Could not submit review.');
            })
            .finally(() => setSubmittingReview(false));
    }

    if (productLoadFailed && id) {
        return (
            <div className="show-one-product-container">
                <p>Product not found.</p>
                <Link to="/show-all-products" className="product-detail-back">← Back to Products</Link>
            </div>
        );
    }
    if (!product) {
        return (
            <div className="show-one-product-container">
                <p>Loading...</p>
            </div>
        );
    }

    const el = product;
    return (
        <div className="show-one-product-container">
            <Link to="/show-all-products" className="product-detail-back">← Back to Products</Link>

            <div className="product-info">
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                        addToCart(el);
                        setJustAddedToCartId(el._id);
                        window.setTimeout(() => setJustAddedToCartId((cur) => (cur === el._id ? null : cur)), 1500);
                    }}
                >
                    {justAddedToCartId === el._id ? 'Added!' : 'Add to Cart'}
                </button>
                <img
                    src={el.image || `https://picsum.photos/seed/${el._id}/400/400`}
                    alt={el.title}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://picsum.photos/seed/${el._id}/400/400`;
                    }}
                />
                <p>Title: {el.title}</p>
                <p>Category: {el.category}</p>
                <p className="product-price">Price: ${Number(el.price).toFixed(2)}</p>
                <p>Rate: {el.rating?.rate ?? 0} | Count: {el.rating?.count ?? 0}</p>
            </div>

            <div className="reviews-container">
                <h4>Ratings & Reviews</h4>
                <div className="reviews-summary">
                    <div className="reviews-summary-stars">{renderStars(el.rating?.rate)}</div>
                    <div className="reviews-summary-meta">{el.rating?.count ?? 0} reviews</div>
                </div>

                <form className="review-form" onSubmit={handleSubmitReview}>
                    <label>Name</label>
                    <input
                        type="text"
                        value={reviewForm.reviewerName}
                        onChange={(e) => setReviewForm((f) => ({ ...f, reviewerName: e.target.value }))}
                        placeholder="Anonymous"
                    />
                    <label>Rating</label>
                    <select
                        value={reviewForm.rating}
                        onChange={(e) => setReviewForm((f) => ({ ...f, rating: Number(e.target.value) }))}
                    >
                        {[5, 4, 3, 2, 1].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                    <label>Review</label>
                    <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                        placeholder="Write your review..."
                        rows={4}
                    />
                    <button type="submit" className="review-submit" disabled={submittingReview}>
                        {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </button>
                </form>

                <div className="reviews-list">
                    <h5>Latest Reviews</h5>
                    {reviewsLoading ? (
                        <p>Loading reviews...</p>
                    ) : reviews.length === 0 ? (
                        <p>No reviews yet. Be the first!</p>
                    ) : (
                        reviews.map((r) => (
                            <div key={r._id} className="review-card">
                                {renderStars(r.rating)}
                                <div className="review-meta">
                                    <strong>{r.reviewerName || 'Anonymous'}</strong>
                                    <span className="review-date">
                                        {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                                    </span>
                                </div>
                                {r.comment && <p className="review-comment">{r.comment}</p>}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDetail;
